/**
 * Nombre y rol que se le enseñan a una persona, sin inventarse ninguno de los dos.
 *
 * El problema que resuelve esto es que Auth0 rellena `name` con el CORREO cuando la
 * cuenta se crea sin nombre —que es lo que pasa en cada alta desde la pantalla de
 * miembros—. Así que `user.name` a secas no es un nombre: es el correo, y la interfaz
 * acababa enseñándolo dos veces seguidas, arriba como nombre y debajo como correo.
 *
 * La salida de emergencia que se había usado hasta ahora era peor en los dos sitios:
 *
 *   - en la lista de miembros se enseñaba el `user_id` de Auth0 (`auth0|6aa130849a82…`),
 *     que no identifica a nadie para quien lo lea;
 *   - en la cabecera del perfil había un `'Mari Angeles'` incrustado en el código, el
 *     nombre de una persona real, que le salía a cualquiera sin nombre ni apodo.
 *
 * Auth0 ya da lo que hace falta: `nickname` es la parte de delante de la @ (`rgarcia`),
 * que es justo lo que se ve en el widget de la barra lateral y lo que la gente reconoce.
 *
 * Va aquí, y no en cada componente, porque los dos sitios tienen que coincidir: el
 * nombre de la fila de miembros y el de la cabecera del perfil son la misma persona.
 */

/** Lo que se necesita de un usuario de Auth0 para sacarle un nombre. */
export interface FuenteDeNombre {
  name?: string | null;
  given_name?: string | null;
  nickname?: string | null;
  preferred_username?: string | null;
  email?: string | null;
}

function texto(valor: unknown): string {
  return typeof valor === 'string' ? valor.trim() : '';
}

/**
 * Nombre para enseñar, o cadena vacía si no hay absolutamente nada de lo que sacarlo.
 *
 * El orden es: un nombre de verdad, el apodo, y la parte local del correo. Un candidato
 * que sea el correo entero se descarta —es el relleno de Auth0, no un nombre—, y por eso
 * no vale con un `||` encadenado: hay que comparar cada candidato con el correo.
 */
export function nombreVisible(user: FuenteDeNombre | null | undefined): string {
  const correo = texto(user?.email);
  const esElCorreo = (valor: string) => !!correo && valor.toLowerCase() === correo.toLowerCase();

  for (const candidato of [user?.name, user?.given_name, user?.nickname, user?.preferred_username]) {
    const limpio = texto(candidato);
    if (limpio && !esElCorreo(limpio)) {
      return limpio;
    }
  }

  // La parte local del correo: `rgarcia@enecworld.com` → `rgarcia`.
  return correo.split('@')[0] || '';
}

/**
 * Iniciales para el avatar, a partir del nombre visible.
 *
 * Dos palabras dan la primera de cada una («Raúl García» → `RG`); una sola da sus dos
 * primeras letras (`rgarcia` → `RG`).
 */
export function inicialesVisibles(user: FuenteDeNombre | null | undefined): string {
  const nombre = nombreVisible(user);
  if (!nombre) {
    return '?';
  }

  const partes = nombre.split(/\s+/).filter(Boolean);
  if (partes.length > 1) {
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  }

  return nombre.slice(0, 2).toUpperCase();
}

/**
 * Etiqueta del rol cuando no hay rol.
 *
 * No es cosmética. Sin asignación en `integrations.users` del tenant la persona entra a
 * una aplicación sin una sola pestaña; enseñarle «usuario» —o «admin» en la fila de
 * miembros— era lo que impedía darse cuenta. Se dice que no tiene rol.
 */
export const SIN_ROL = 'sin rol';
